import React, { ReactElement, useContext, useMemo, useState } from "react";
import { Typography, Box } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { confirmResetPassword } from "aws-amplify/auth";
import enumStrings, { RoutePaths } from "../../../enumStrings";
import { commonCognitoPasswordRegex } from "../../common/utilities/commonPasswordRegex";
import PasswordField from "../../common/PasswordField/PasswordField";
import { commonStyles } from "../../common/commonStyles";
import Button from "../../common/Button";
import TextField from "../../common/TextField";
import LinkText from "../../common/LinkText";
import { signInHandler } from "./dashboardSignIn";
import { getApolloContext } from "@apollo/client";

import { useNavigate } from "react-router-dom";
import { setPendingNewPasswordChallenge } from "./pendingNewPasswordChallenge";
import { getAuthErrorName, getSignInErrorMessage } from "./authErrors";
import AuthErrorMessage from "./AuthErrorMessage";

interface ResetPasswordInput {
  code: string;
  newPassword: string;
  confirmPassword: string;
}

const ResetPassword = ({ email }: { email: string }): ReactElement => {
  const apolloClient = useContext(getApolloContext());
  const navigate = useNavigate();

  const initialValues: ResetPasswordInput = {
    code: "",
    newPassword: "",
    confirmPassword: "",
  };

  const validationSchema = yup.object({
    code: yup.string().required(enumStrings.pages.common.validation.code),
    newPassword: yup
      .string()
      .required(enumStrings.pages.common.validation.newPassword)
      .matches(
        commonCognitoPasswordRegex,
        enumStrings.pages.common.validation.mustContain8Characters
      ),
    confirmPassword: yup
      .string()
      .oneOf(
        [yup.ref("newPassword"), undefined],
        enumStrings.pages.common.validation.passwordsMustMatch
      )
      .required(enumStrings.pages.common.validation.confirmPassword),
  });

  const [customError, setCustomError] = useState("");
  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [stage, setStage] = useState(1);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: ResetPasswordInput) => {
      setCustomError("");
      setLoader(true);
      try {
        await confirmResetPassword({
          username: email,
          confirmationCode: input.code,
          newPassword: input.newPassword,
        });
        setStage(2);
      } catch (error: unknown) {
        switch (getAuthErrorName(error)) {
          case "LimitExceededException":
          case "TooManyRequestsException":
            setCustomError(enumStrings.pages.common.errors.tooManyAttempts);
            break;
          case "CodeMismatchException":
          case "UserNotFoundException":
            setCustomError(enumStrings.pages.common.errors.invalidVerificationCode);
            break;
          case "ExpiredCodeException":
            setCustomError("This verification code has expired. Select Back to sign in, then select Forgot your password? to get a new code.");
            break;
          case "InvalidPasswordException":
            setCustomError(
              `This password cannot be used. ${enumStrings.pages.common.validation.mustContain8Characters} Then try again.`
            );
            break;
          case "NetworkError":
            setCustomError(enumStrings.pages.common.errors.networkError);
            break;
          default:
            setCustomError("We could not reset your password. Try again. If it keeps happening, contact your chamber administrator.");
        }
      } finally {
        setLoader(false);
      }
    },
  });

  const enumStringEmail = useMemo(() => {
    let emailPart1 = email.split("@")[0].split("+")[0];
    let emailPart2 = email.split("@")[1];
    return `${emailPart1}@${emailPart2}`;
  }, [email]);

  return (
    <>
      {stage === 1 && (
        <form
          style={{
            width: "100%",
          }}
          onSubmit={formik.handleSubmit}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: commonStyles.fontSizes.medium,
                pb: 2,
              }}
            >
              {enumStrings.pages.resetPassword.pleaseVerifyEmailText(
                enumStringEmail
              )}
            </Typography>

            <TextField
              id="code"
              name="code"
              label="Verification Code"
              value={formik.values.code}
              onChange={formik.handleChange}
              error={formik.touched.code && Boolean(formik.errors.code)}
              helperText={formik.touched.code && formik.errors.code}
              fullWidth
            />
            <PasswordField
              id="newPassword"
              fieldName="newPassword"
              label="New Password"
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              error={formik.errors.newPassword}
              touched={formik.touched.newPassword}
            />
            <PasswordField
              id="confirmPassword"
              fieldName="confirmPassword"
              label="Confirm Password"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              error={formik.errors.confirmPassword}
              touched={formik.touched.confirmPassword}
            />
            <AuthErrorMessage message={customError} />
            <Button
              text={enumStrings.pages.resetPassword.buttonText}
              disabled={buttonDisabled || loader}
              isLoading={loader}
              fullWidth
              size="large"
              type="submit"
              additionalSx={{
                mt: 4,
              }}
            />
            <LinkText
              to={RoutePaths.login}
              text={enumStrings.pages.common.buttons.goBackToLoginText}
            />
          </Box>
        </form>
      )}
      {stage === 2 && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: commonStyles.fontSizes.medium,
              pb: 2,
            }}
          >
            {enumStrings.pages.resetPassword.resetPasswordSuccessText}
          </Typography>

          <AuthErrorMessage message={customError} />
          <Button
            text={enumStrings.pages.resetPassword.signInButton}
            fullWidth
            size="large"
            type="button"
            isLoading={loader}
            disabled={buttonDisabled || loader}
            additionalSx={{
              mt: 4,
            }}
            onClick={async () => {
              setCustomError("");
              setLoader(true);
              try {
                await signInHandler(email, formik.values.newPassword, apolloClient, (challengeEmail) => {
                  setPendingNewPasswordChallenge(challengeEmail);
                  navigate(RoutePaths.newPassword);
                });
                setButtonDisabled(true);
              } catch (error: unknown) {
                setCustomError(getSignInErrorMessage(error));
              } finally {
                setLoader(false);
              }
            }}
          />
        </Box>
      )}
    </>
  );
};

export default ResetPassword;
